import immutableStore from 'utils/immutableStore';
import {List, Record} from 'immutable';
import AdminTopicEditorPageActions from 'actions/admin/AdminTopicEditorPageActions';
import alt from 'components/Dispatcher';
import NewFilterWord from 'records/admin/topics/NewFilterWord';

class AdminTopicEditorState extends Record({
  // current alts as a list. the user edits these, and they're committed to the current topic
  // before we send to the server.
  alternateSpellings: List(),
  topic: undefined, // topic being edited, including committed changes.
  originalTopic: undefined, // copy of the topic being edited, as it was when the editor started.
  editingNewTopic: false, // is this a new topic?
  editingTopicName: false, // is the name editor open?
  // the current name of the topic. this is what the user edits.
  topicName: '',
  // Show errors around required fields which aren't populated.
  showErrors: false,
}) {}

@immutableStore
class AdminTopicEditorPageStore {
  constructor() {
    this.state = new AdminTopicEditorState();
    this.bindActions(AdminTopicEditorPageActions);
  }

  appendNewAlternateSpelling(newWord) {
    const {alternateSpellings} = this.state;
    this.setState({
      alternateSpellings: alternateSpellings.push(newWord),
    });
  }

  deleteAlternateSpellingAtIndex(index) {
    const {alternateSpellings} = this.state;
    this.setState({
      alternateSpellings: alternateSpellings.delete(index),
    });
  }

  openTopicNameEditor() {
    const {topic} = this.state;
    this.setState({
      editingTopicName: true,
      topicName: topic.word, // just in case.
    });
  }

  /**
   * Reset the name editor state.
   *
   * Disable the name editor, and set the name to the name of the current topic.
   */
  resetTopicNameEditor() {
    const {topic} = this.state;
    this.setState({
      editingTopicName: false,
      topicName: topic.word,
    });
  }

  /** Save displayWhen to the current topic. */
  saveDisplayWhen(displayWhen) {
    const {topic} = this.state;
    const newTopic = topic.set('displayWhen', displayWhen);
    this.setState({
      topic: newTopic,
    });
  }

  /**
   * Save speakerMatch to the current topic.
   */
  saveSpeakerMatch(speakerMatch) {
    const {topic} = this.state;
    const newTopic = topic.set('speakerMatch', speakerMatch);
    this.setState({
      topic: newTopic,
    });
  }

  /** Save the current topic name to the current topic. */
  saveTopicName() {
    const {topic, topicName} = this.state;
    const trimmed = topicName.trim();
    const newTopic = topic.set('word', trimmed);
    this.setState({
      topic: newTopic,
    });
  }

  setTopicName(topicName) {
    this.setState({
      topicName,
    });
  }

  setupTopicEditorForExistingTopic([topic, editingTopicName]) {
    this.setState({
      alternateSpellings: topic.alternateSpellings.toList(),
      topic,
      originalTopic: topic,
      editingNewTopic: false,
      editingTopicName,
      topicName: topic.word,
    });
  }

  setupTopicEditorForNewTopic() {
    const topic = new NewFilterWord({
      word: '',
      displayWhen: 'ALWAYS',
      topic: true,
      speakerMatch: 'ALL',
    });
    this.setState({
      alternateSpellings: List(),
      topic,
      originalTopic: topic,
      editingNewTopic: true,
      // Open the name editor for the as-yet-unnamed topic.
      editingTopicName: true,
      topicName: topic.word,
    });
  }

  updateAlternateSpellingAtIndex([index, newValue]) {
    const {alternateSpellings} = this.state;
    this.setState({
      alternateSpellings: alternateSpellings.set(index, newValue),
    });
  }

  updateIncludeInInsights(includeInInsights) {
    this.setState({
      topic: this.state.topic.set('includeInInsights', includeInInsights),
    });
  }

  showErrors() {
    this.setState({showErrors: true});
  }

  hideErrors() {
    this.setState({showErrors: false});
  }
}

export default alt.createStore(AdminTopicEditorPageStore);
